/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React from "react";

import { Trans, useI18n } from "@bookingcom/lingojs-react";
import { Box, Button, Card, Icon, Link, SheetContainer, Stack, Text, Title, useToast } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { InfoSignIcon, PopoutIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import { useFormatDateTime } from "@bookingcom/flights-core";
import { ryanairUtils } from "@flights/web-api-utils-universal";

import { UICarrierCheckinInfo, UIFlightSegment, UILeg } from "@flights/types";

// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../../../store";
import { getPassengerFullNameByReference } from "../../../store/selectors/passengers";

import useUserAgent from "../../../hooks/useUserAgent";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useEventTracking from "../../../hooks/useEventTracking";

import { CheckinInfoCardItem } from "./CheckinInfoCardItem";

import { SPACER_PADDED } from "@flights/constants";

import styles from "./CheckinInfoCard.module.css";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CopyIcon } from "@bookingcom/bui-assets-react/streamline";
import { trackCheckInLinks } from "../../../utils/experiments/post-booking/flights_pb_universal_checkin_links_cirium";

interface CheckInInfoCardProps {
  openCheckin?: boolean;
  checkInInfo?: UICarrierCheckinInfo;
  segment: UIFlightSegment;
  segmentIndex: number;
  leg: UILeg;
  legIndex: number;
  showHeader?: boolean;
  showVuelingAlert?: boolean;
  isUpdatedCheckInPageExperiment?: boolean;
}

export const CheckInInfoCard: React.FC<CheckInInfoCardProps> = ({
  openCheckin,
  checkInInfo,
  leg,
  legIndex,
  showHeader,
  segment,
  segmentIndex,
  isUpdatedCheckInPageExperiment
}) => {
  const i18n = useI18n();
  const { formats } = useFormatDateTime(i18n);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { isMobile } = useUserAgent();
  const { requestId } = useGlobalContext();
  const { show: showToast } = useToast();
  const trackV2 = useEventTracking("pb_webcheckin", requestId);
  const { order } = store.order;

  const [showEmailInfo, setShowEmailInfo] = React.useState(false);
  const [showCheckinSheet, setShowCheckinSheet] = React.useState(false);

  if (!order) {
    return null;
  }

  const { airOrder, booker } = order;
  const { airTicketsByLeg = [], flightSegments } = airOrder;

  // Finding flight reference by segment and leg index
  const flightReference = airOrder.airlineReferencesByLeg?.find((reference) => {
    return reference.legIdentifier.find((identifier) => {
      return identifier.segmentIndex === segmentIndex && identifier.legIndex === legIndex;
    });
  });
  // 1. Booking reference
  const reference = flightReference?.reference;

  // 2. Flight number
  const flightNumber = leg.carriersData[0].code + leg.flightInfo.flightNumber;

  // 3. Booker Email Address
  const bookerEmailAddress = booker?.email;

  // 4. Check in email
  const checkinEmail = flightReference?.checkinEmail;

  const isRyanairLeg = ryanairUtils.isRyanairLegs([leg]);

  const emailToDisplay = isRyanairLeg ? bookerEmailAddress : checkinEmail;

  // 5. Ticket numbers
  const airtickets = airTicketsByLeg.filter((airticket) =>
    airticket.legIdentifiers.find(
      (legIdentifier) => legIdentifier.segmentIndex === segmentIndex && legIdentifier.legIndex === legIndex
    )
  );

  const handleCheckinClick = () => {
    trackV2("click_checkin", {
      order_id: order.orderId,
      segment: segmentIndex,
      legs: [...flightSegments[segmentIndex].legs.keys()]
    });
    trackCheckInLinks.goals.CHECKIN_VIA_AIRLINE_WEBSITE_CLICK();
  };

  const checkInLink = checkInInfo?.checkinLink;

  let destinationLeg = leg;
  let i = legIndex + 1;
  // Finds what is the destination within the checkin window.
  // Two examples on how this functionality works for the first leg (legIndex = 0)
  // Example 1: legs: [{ departure: Amsterdam, arrival: London }, { departure: London, arrival: Lisbon }, { departure: Lisbon, arrival: Berlin }]
  // In this case all legs are sharing same check in so the departure would be Amsterdam and arrival would be Berlin
  // Example 2: legs: [{ departure: Amsterdam, arrival: London }, { departure: London, arrival: Lisbon vi: true }, { departure: Lisbon, arrival: Berlin }]
  // In this case route is the same, but the second leg has VI, hence you have to check in for the third leg separately
  // so departure would be Amsterdam and arrival would be Lisbon
  while (segment.legs[i]) {
    destinationLeg = segment.legs[i];
    if (destinationLeg.vi?.protected) {
      break;
    }
    i++;
  }

  const handleCheckinSheetClick = () => {
    setShowCheckinSheet(true);
  };

  const handleProceedCheckin = () => {
    setShowCheckinSheet(false);
    handleCheckinClick();
    window.open(checkInLink, "_blank");
  };

  const departureTime = leg.updatedDepartureTime ? leg.updatedDepartureTime : leg.departureTime;

  return (
    <>
      {showHeader && (
        <Stack gap={0}>
          <Title
            variant="strong_1"
            title={i18n.trans(
              t("flights_pb_checkin_vi_route_subtitle", {
                variables: {
                  origin_city: leg.departureAirport.cityName || "",
                  destination_city: leg.arrivalAirport.cityName || ""
                }
              })
            )}
          />
          <Text variant="body_2" color="neutral_alt">
            {i18n.trans(
              t("flights_pb_checkin_route_itinerary", {
                variables: {
                  airline_name: leg.carriersData[0].name,
                  departure_date: [formats.flightDateWeekday(departureTime), formats.flightTime(departureTime)].join(
                    SPACER_PADDED
                  )
                }
              })
            )}
          </Text>
        </Stack>
      )}

      <Card>
        <Stack gap={4}>
          {reference && (
            <Stack direction="row" gap={2}>
              <Stack.Item shrink>
                <Text className={styles.text} variant="body_2">
                  {isRyanairLeg
                    ? i18n.trans(t("flights_pb_ryanair_reservation_number"))
                    : i18n.trans(t("flights_pb_checkin_booking_reference"))}
                </Text>
              </Stack.Item>
              <Stack.Item shrink>
                <CheckinInfoCardItem
                  text={reference}
                  arialLabel={i18n.trans(t("a11y_flights_pb_checkin_copy_booking_reference"))}
                  onCopySuccessText={i18n.trans(t("flights_pb_checkin_copy_success_booking_reference"))}
                />
              </Stack.Item>
            </Stack>
          )}

          {flightNumber && (
            <Stack direction="row" gap={2}>
              <Text className={styles.text} variant="body_2">
                {i18n.trans(t("flights_pb_checkin_flight_number"))}
              </Text>
              <Stack.Item shrink>
                <CheckinInfoCardItem
                  text={flightNumber}
                  arialLabel={i18n.trans(t("a11y_flights_pb_checkin_copy_flight_number"))}
                  onCopySuccessText={i18n.trans(t("flights_pb_checkin_copy_success_flight_number"))}
                />
              </Stack.Item>
            </Stack>
          )}

          {emailToDisplay && (
            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <Text className={styles.text} variant="body_2">
                  {i18n.trans(t("flights_pb_checkin_email"))}
                </Text>
                <Stack.Item shrink>
                  <CheckinInfoCardItem
                    text={emailToDisplay}
                    arialLabel={i18n.trans(t("a11y_flights_pb_checkin_copy_email"))}
                    onCopySuccessText={i18n.trans(t("flights_pb_checkin_copy_success_email"))}
                  />
                </Stack.Item>
              </Stack>
              {!isRyanairLeg && (
                <Link
                  text={
                    isUpdatedCheckInPageExperiment
                      ? i18n.trans(t("flights_pb_checkin_alias_email_howto"))
                      : i18n.trans(t("flights_pb_checkin_email_question"))
                  }
                  onClick={() => setShowEmailInfo(true)}
                  attributes={
                    isUpdatedCheckInPageExperiment
                      ? {
                          "aria-label": i18n.trans(t("a11y_flights_pb_checkin_alias_email_howto"))
                        }
                      : {}
                  }
                />
              )}
              <SheetContainer
                active={showEmailInfo}
                position={isMobile ? "bottom" : "center"}
                onClose={() => setShowEmailInfo(false)}
                onCloseTrigger={() => setShowEmailInfo(false)}
                closeAriaLabel={i18n.trans(t("a11y_flights_pb_checkin_email_overlay_close"))}
                title={
                  isUpdatedCheckInPageExperiment
                    ? i18n.trans(t("flights_pb_checkin_alias_email_popup_title"))
                    : i18n.trans(t("flights_pb_checkin_email_question"))
                }
              >
                {isUpdatedCheckInPageExperiment ? (
                  <Stack gap={4}>
                    <Box padding={4} borderRadius={200} borderColor="neutral_alt">
                      <Stack direction="row" justifyContent="space-between" alignItems="center" wrap="nowrap">
                        <Stack.Item shrink>
                          <Text variant="strong_2" attributes={{ style: { wordBreak: "break-all" } }}>
                            {emailToDisplay}
                          </Text>
                        </Stack.Item>
                        <CopyToClipboard text={emailToDisplay}>
                          <Button
                            attributes={{ "aria-label": i18n.trans(t("a11y_flights_pb_checkin_copy_email")) }}
                            variant="tertiary-neutral"
                            icon={<Icon svg={CopyIcon} />}
                            onClick={() => showToast({ text: i18n.trans(t("flights_pb_checkin_copy_success_email")) })}
                          />
                        </CopyToClipboard>
                      </Stack>
                    </Box>
                    <Stack gap={2}>
                      <Stack gap={1}>
                        <Text variant="strong_2">
                          <Trans tag="flights_pb_checkin_alias_email_popup_q_when" />
                        </Text>
                        <Text>
                          <Trans tag="flights_pb_checkin_alias_email_popup_a_when" />
                        </Text>
                      </Stack>

                      <Stack gap={1}>
                        <Text variant="strong_2">
                          <Trans tag="flights_pb_checkin_alias_email_popup_q_login" />
                        </Text>
                        <Text>
                          <Trans tag="flights_pb_checkin_alias_email_popup_a_login" />
                        </Text>
                      </Stack>

                      {bookerEmailAddress && (
                        <Stack gap={1}>
                          <Text variant="strong_2">
                            <Trans tag="flights_pb_checkin_alias_email_popup_q_boarding_pass" />
                          </Text>
                          <Text>
                            <Trans
                              tag="flights_pb_checkin_alias_email_popup_a_boarding_pass"
                              variables={{
                                contact_email: bookerEmailAddress
                              }}
                            />
                          </Text>
                        </Stack>
                      )}
                    </Stack>

                    <Stack.Item>
                      <Button
                        attributes={{ "aria-label": i18n.trans(t("a11y_flights_pb_checkin_email_overlay_cta")) }}
                        wide={isMobile}
                        size={isMobile ? "large" : "medium"}
                        onClick={() => setShowEmailInfo(false)}
                      >
                        {i18n.trans(t("flights_pb_checkin_email_overlay_cta"))}
                      </Button>
                    </Stack.Item>
                  </Stack>
                ) : (
                  <Stack gap={2}>
                    <Stack.Item>
                      <Trans
                        tag={"flights_pb_checkin_email_overlay_body"}
                        variables={{ contact_email: bookerEmailAddress || "", start_bold: "<0>", end_bold: "</0>" }}
                        components={[<strong key={0} />]}
                      />
                    </Stack.Item>

                    <Stack.Item>
                      <Button
                        attributes={{ "aria-label": i18n.trans(t("a11y_flights_pb_checkin_email_overlay_cta")) }}
                        wide={isMobile}
                        size={isMobile ? "large" : "medium"}
                        onClick={() => setShowEmailInfo(false)}
                      >
                        {i18n.trans(t("flights_pb_checkin_email_overlay_cta"))}
                      </Button>
                    </Stack.Item>
                  </Stack>
                )}
              </SheetContainer>
            </Stack>
          )}

          {airtickets?.length > 0 &&
            airtickets.map((airticket, index) => {
              if (!airticket.ticketNumber) {
                return null;
              }

              const fullName = getPassengerFullNameByReference(i18n, airticket.travelerReference, order?.passengers);

              return (
                <Stack key={index} direction="row" gap={2}>
                  <Text className={styles.text} variant="body_2">
                    {i18n.trans(
                      t("flights_pb_checkin_eticket_number", {
                        variables: {
                          traveller_full_name: fullName
                        }
                      })
                    )}
                  </Text>
                  <Stack.Item shrink>
                    <CheckinInfoCardItem
                      text={airticket.ticketNumber}
                      arialLabel={i18n.trans(
                        t("a11y_flights_pb_checkin_copy_eticket_number", {
                          variables: {
                            traveller_full_name: fullName
                          }
                        })
                      )}
                      onCopySuccessText={i18n.trans(
                        t("flights_pb_checkin_copy_success_eticket_number", {
                          variables: {
                            traveller_full_name: fullName
                          }
                        })
                      )}
                    />
                  </Stack.Item>
                </Stack>
              );
            })}
        </Stack>
      </Card>
      {!checkInLink && (
        <>
          {!openCheckin && (
            <Stack alignItems={isMobile ? undefined : "center"} direction="row">
              {isMobile ? (
                <Box padding={1}>
                  <Icon svg={InfoSignIcon} />
                </Box>
              ) : (
                <Icon svg={InfoSignIcon} />
              )}
              <Stack.Item grow>
                <Text variant="body_2">
                  <Trans
                    tag="flights_pb_bookingdetails_precheckin_airline_website_note"
                    variables={{ airline_name: leg.carriersData[0].name, start_bold: "<0>", end_bold: "</0>" }}
                    components={[<strong key={0} />]}
                  />
                </Text>
              </Stack.Item>
            </Stack>
          )}
          {openCheckin && (
            <Stack alignItems="center" direction="row">
              <Icon svg={InfoSignIcon} />
              <Stack.Item grow>
                <Text variant="body_2">
                  <Trans
                    tag="flights_pb_bookingdetails_checkin_airline_website_note"
                    variables={{ airline_name: leg.carriersData[0].name, start_bold: "<0>", end_bold: "</0>" }}
                    components={[<strong key={0} />]}
                  />
                </Text>
              </Stack.Item>
            </Stack>
          )}
        </>
      )}
      {isRyanairLeg && (
        <Stack alignItems="center" direction="row">
          <Icon svg={InfoSignIcon} />
          <Stack.Item grow>
            <Text variant="body_2">{i18n.trans(t("flights_pb_checkin_ryanair_website_note"))}</Text>
          </Stack.Item>
        </Stack>
      )}
      {checkInLink && (
        <Stack direction={isMobile ? "column-reverse" : "row"} justifyContent="end">
          {!openCheckin && (
            <Button
              size="large"
              variant="secondary"
              href={isUpdatedCheckInPageExperiment ? undefined : checkInLink}
              attributes={{
                target: isUpdatedCheckInPageExperiment ? undefined : "_blank",
                "aria-label": i18n.trans(t("a11y_flights_pb_precheckin_airline_website_cta"))
              }}
              iconPosition={isUpdatedCheckInPageExperiment ? undefined : "end"}
              icon={isUpdatedCheckInPageExperiment ? undefined : <Icon svg={<PopoutIcon />} />}
              onClick={isUpdatedCheckInPageExperiment ? handleCheckinSheetClick : handleCheckinClick}
            >
              {i18n.trans(t("flights_pb_precheckin_airline_website_cta"))}
            </Button>
          )}
          {openCheckin && (
            <Button
              size="large"
              variant="primary"
              href={checkInLink}
              attributes={{
                target: "_blank",
                "aria-label": i18n.trans(t("a11y_flights_pb_checkin_airline_website_cta"))
              }}
              iconPosition="end"
              icon={<Icon svg={<PopoutIcon />} />}
              onClick={handleCheckinClick}
            >
              {i18n.trans(t("flights_pb_checkin_airline_website_cta"))}
            </Button>
          )}
        </Stack>
      )}
      <SheetContainer
        active={showCheckinSheet}
        position={isMobile ? "bottom" : "center"}
        onClose={() => setShowCheckinSheet(false)}
        onCloseTrigger={() => setShowCheckinSheet(false)}
        closeAriaLabel={i18n.trans(t("a11y_flights_pb_checkin_early_popup_close"))}
        title={i18n.trans(t("flights_pb_checkin_early_popup_title"))}
      >
        <Stack attributes={{ style: { marginTop: "var(--bui_spacing_3x)" } }} gap={4}>
          <Text>
            <Trans tag="flights_pb_checkin_early_popup_desc" />
          </Text>
          <Button
            attributes={{ "aria-label": i18n.trans(t("flights_pb_checkin_sheet_proceed")) }}
            size="large"
            variant="primary"
            wide={isMobile}
            iconPosition="end"
            icon={<Icon svg={<PopoutIcon />} />}
            onClick={handleProceedCheckin}
          >
            {i18n.trans(t("flights_pb_checkin_early_popup_continue"))}
          </Button>
        </Stack>
      </SheetContainer>
    </>
  );
};
